import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Gauge, Boxes, Flame, Factory, BrainCircuit, Rocket, Sliders, ChevronDown, Wifi, WifiOff, Lock, Check, Database, LogOut } from "lucide-react";
import { useApp } from "@/context/AppStore";
import { useAuth } from "@/context/AuthStore";
import { AdminPanel } from "@/components/AdminPanel";
import { LOGOUT } from "@/constants/testIds";

const NAV = [
  { to: "/", label: "Command", icon: Gauge, testid: "nav-command-center", end: true },
  { to: "/coal", label: "Coal Intel", icon: Boxes, testid: "nav-coal" },
  { to: "/boiler", label: "Boiler Twin", icon: Flame, testid: "nav-boiler" },
  { to: "/production", label: "Production", icon: Factory, testid: "nav-production" },
  { to: "/advisor", label: "AI Advisor", icon: BrainCircuit, testid: "nav-advisor" },
  { to: "/future", label: "Future", icon: Rocket, testid: "nav-future" },
];

export function Shell({ children }) {
  const { roleMeta, sim, system, snapshot, connected } = useApp();
  const { user, logout } = useAuth();
  const [adminOpen, setAdminOpen] = useState(false);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [liveNote, setLiveNote] = useState(false);

  // Live source state (updates every poll); plant name from system info.
  const isSim = snapshot?.is_simulation ?? system?.is_simulation ?? true;
  const plantName = system?.plant_name || "Akry Organics";

  return (
    <div className="min-h-screen flex flex-col">
      {/* top bar */}
      <header className="panel border-b border-border sticky top-0 z-30 backdrop-blur" style={{ background: "rgba(10,18,36,0.92)" }}>
        <div className="flex items-center h-14 px-4 gap-4">
          <div className="flex items-center gap-3 shrink-0">
            <img src="/brand/ergoniq-logo.svg" alt="Ergoniq" className="h-11 w-auto"
              style={{ filter: "brightness(0) invert(1)" }} data-testid="logo" />
            <div className="h-8 w-px bg-border" />
            <div className="leading-none">
              <div className="font-display font-extrabold text-sm tracking-tight text-ink">OIM-AKRY</div>
              <div className="text-[0.58rem] font-mono text-ink-soft tracking-wider">COAL→PRODUCT INTELLIGENCE</div>
            </div>
          </div>

          {/* nav */}
          <nav className="flex items-center gap-0 ml-1 overflow-x-auto flex-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-2.5 py-2 text-[0.74rem] font-medium whitespace-nowrap transition-colors duration-200 border-b-2 ${
                    isActive ? "text-brand-green border-brand-green" : "text-ink-soft border-transparent hover:text-ink"
                  }`}>
                <n.icon size={14} />
                {n.label}
              </NavLink>
            ))}
          </nav>

          {/* data source selector */}
          <div className="relative shrink-0 hidden md:block">
            <button onClick={() => { setSourceOpen((v) => !v); setLiveNote(false); }} data-testid="data-source-badge"
              title={isSim ? "Synthetic simulation feed" : `Live plant feed · ${plantName}`}
              className="flex items-center gap-2 px-2.5 py-1.5 panel-2 hover:opacity-90 transition-opacity"
              style={{ borderColor: isSim ? "rgba(250,178,11,0.4)" : "rgba(51,183,109,0.45)" }}>
              <span className="live-dot inline-block w-1.5 h-1.5 rounded-full" style={{ background: isSim ? "#fab20b" : "#33b76d" }} />
              <span className="text-[0.62rem] font-mono font-bold uppercase tracking-wider" style={{ color: isSim ? "#fab20b" : "#33b76d" }}>
                {isSim ? "Simulation" : "Live"}
              </span>
              <span className="text-[0.62rem] font-mono text-ink-soft hidden xl:inline">· {plantName}</span>
              <ChevronDown size={12} className="text-ink-soft" />
            </button>
            <AnimatePresence>
              {sourceOpen && (
                <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                  className="absolute right-0 mt-1.5 w-72 panel z-40 p-1.5" data-testid="data-source-menu">
                  <div className="eyebrow px-2 py-1.5">Data Source · {plantName}</div>

                  {/* Simulation (active, selectable) */}
                  <button data-testid="ds-option-simulation" onClick={() => setSourceOpen(false)}
                    className="w-full text-left px-3 py-2.5 bg-surface-2 flex items-start gap-2.5">
                    <Database size={15} className="text-brand-gold mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[0.8rem] font-medium text-ink">Simulation</span>
                        {isSim && <Check size={13} className="text-brand-green" />}
                      </div>
                      <div className="text-[0.66rem] text-ink-soft mt-0.5">Synthetic, physics-valid feed · anomaly testing</div>
                    </div>
                  </button>

                  {/* Live (disabled) */}
                  <button data-testid="ds-option-live"
                    onClick={() => setLiveNote(true)}
                    className="w-full text-left px-3 py-2.5 mt-0.5 flex items-start gap-2.5 cursor-not-allowed opacity-70">
                    <Lock size={15} className="text-ink-soft mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[0.8rem] font-medium text-ink-soft">Live Plant</span>
                        <span className="text-[0.54rem] font-mono px-1.5 py-0.5 border border-ink-soft/30 text-ink-soft uppercase tracking-wider">Locked</span>
                      </div>
                      <div className="text-[0.66rem] text-ink-soft mt-0.5">Real PLC / NIR / historian feed</div>
                    </div>
                  </button>

                  <div data-testid="live-note" className="mx-2 mt-1.5 mb-1 px-2.5 py-2 text-[0.66rem] font-mono leading-relaxed"
                    style={{ background: liveNote ? "rgba(250,178,11,0.12)" : "transparent", color: liveNote ? "#fab20b" : "#8BA1C0", border: liveNote ? "1px solid rgba(250,178,11,0.35)" : "1px solid transparent" }}>
                    Live feed will be active on actual deployment, once the plant data source is connected.
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* connection */}
          <div className="flex items-center gap-1.5 shrink-0" title={connected ? "Live" : "Disconnected"}>
            {connected ? <Wifi size={14} className="text-brand-green" /> : <WifiOff size={14} className="text-alert" />}
          </div>

          {/* admin */}
          {roleMeta.canAdmin && (
            <button onClick={() => setAdminOpen(true)} data-testid="open-admin"
              className="flex items-center gap-1.5 px-3 py-1.5 panel-2 text-[0.72rem] font-mono uppercase tracking-wider text-brand-gold hover:border-brand-gold/60 transition-colors shrink-0">
              <Sliders size={13} /> Control
            </button>
          )}

          {/* profile dropdown */}
          <div className="relative shrink-0">
            <button onClick={() => setProfileOpen((v) => !v)} data-testid="profile-button"
              title={`${user?.name} · ${roleMeta.label}`}
              className="flex items-center gap-1.5 px-1.5 py-1 panel-2 hover:border-brand-green/50 transition-colors">
              <div className="w-7 h-7 flex items-center justify-center text-[0.66rem] font-bold font-mono"
                style={{ background: "rgba(51,183,109,0.16)", color: "#33b76d" }}>{roleMeta.short}</div>
              <ChevronDown size={13} className="text-ink-soft" />
            </button>
            <AnimatePresence>
              {profileOpen && (
                <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                  className="absolute right-0 mt-1.5 w-60 panel z-40 p-1.5" data-testid="profile-menu">
                  <div className="px-3 py-2.5 flex items-center gap-2.5" data-testid="current-user">
                    <div className="w-9 h-9 flex items-center justify-center text-[0.74rem] font-bold font-mono shrink-0"
                      style={{ background: "rgba(51,183,109,0.16)", color: "#33b76d" }}>{roleMeta.short}</div>
                    <div className="leading-tight min-w-0">
                      <div className="text-[0.82rem] font-medium text-ink truncate">{user?.name}</div>
                      <div className="text-[0.62rem] font-mono text-ink-soft uppercase tracking-wider flex items-center gap-1">
                        {roleMeta.label}
                        {roleMeta.canAdmin && <span className="text-brand-gold">· ADMIN</span>}
                      </div>
                      {user?.username && <div className="text-[0.6rem] font-mono text-ink-soft mt-0.5">@{user.username}</div>}
                    </div>
                  </div>
                  <div className="h-px bg-border mx-1 my-1" />
                  <button onClick={() => { setProfileOpen(false); logout(); }} data-testid={LOGOUT.button}
                    className="w-full flex items-center gap-2 px-3 py-2 text-[0.76rem] text-ink-soft hover:text-alert hover:bg-surface-2 transition-colors">
                    <LogOut size={14} /> Sign out
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* simulation banner */}
        {sim.sim_mode_active && (
          <div data-testid="sim-banner"
            className="flex items-center justify-center gap-2 py-1 bg-brand-gold text-black font-mono font-bold uppercase tracking-[0.2em] text-[0.66rem]">
            <span className="w-1.5 h-1.5 rounded-full bg-black live-dot" />
            Simulation Mode Active — Data is Synthesized
            {sim.active_anomalies.length > 0 && <span className="ml-2 px-2 bg-black/20">{sim.active_anomalies.length} anomaly injected</span>}
          </div>
        )}
      </header>

      <main className="flex-1 grid-lines">{children}</main>

      <AdminPanel open={adminOpen} onClose={() => setAdminOpen(false)} />
    </div>
  );
}
